/**
 * Your AI providers — ADR-0073 §6 (V105-2-6), Settings → Security #your-ai-providers.
 *
 * GET/POST/PATCH /ai-providers, POST /ai-providers/:id/evaluate — owner / admin;
 * POST /ai-providers/:id/approve — owner only (V105-2-10 M-3): a provider that passed
 * its check serves customer content only after an owner approves it. Admins see who
 * can approve (no dead end).
 * Keys are write-only (only "Key saved" shows). A provider serves a task only
 * after its check passes for the pinned model; a model change asks for a new check.
 * Other roles see who to ask (no dead end).
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, EmptyState, Field, Input, Select, Spinner } from '@aigovops/design-system';
import {
  approveAiProvider, createAiProvider, evaluateAiProvider, listAiProviders, patchAiProvider,
  type CustomerAiProvider, type CustomerAiProviderInput, type CustomerAiProviderKind,
} from '../lib/api';
import { queryKeys } from '../lib/query-keys';
import { errorText } from './today/shared';
import { AskAdmin } from './AskAdmin';
import './data-protection-settings.css';

const KIND_LABEL: Record<CustomerAiProviderKind, string> = {
  vertex_wif: 'Google Cloud Vertex AI (keyless)', azure_openai: 'Azure OpenAI', openai_compatible: 'OpenAI-compatible endpoint',
};
const STATUS: Record<CustomerAiProvider['status'], { label: string; tone: 'accent' | 'neutral' }> = {
  active: { label: 'Ready', tone: 'accent' }, pending_eval: { label: 'Needs a check', tone: 'neutral' }, disabled: { label: 'Disabled', tone: 'neutral' },
};
const WAITING = { label: 'Waiting for owner approval', tone: 'neutral' as const };
const waitingForOwner = (p: CustomerAiProvider) => p.status === 'active' && p.owner_ack?.state !== 'approved';
const REGIONS = ['us-central1', 'us-east1', 'us-east4', 'us-east5', 'us-south1', 'us-west1', 'us-west4'];
const blank = (): CustomerAiProviderInput & { config: NonNullable<CustomerAiProviderInput['config']> } =>
  ({ kind: 'azure_openai', display_name: '', model_id: '', endpoint_url: '', region: 'us-east4', api_key: '', config: { deployment: '', api_version: '2024-10-21' } });

export function CustomerAiProviders({ slug, role }: { slug: string; role: string }) {
  const qc = useQueryClient();
  const canManage = role === 'owner' || role === 'admin';
  const q = useQuery({ queryKey: queryKeys.customerAiProviders(slug), queryFn: () => listAiProviders(slug), enabled: canManage, retry: false, staleTime: 30_000 });
  const [form, setForm] = useState<ReturnType<typeof blank> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState('');

  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: queryKeys.customerAiProviders(slug) }), qc.invalidateQueries({ queryKey: queryKeys.federalParity(slug) })]);
  const set = (patch: Partial<ReturnType<typeof blank>>) => { setErr(null); setForm((f) => (f ? { ...f, ...patch } : f)); };
  const setCfg = (patch: Partial<ReturnType<typeof blank>['config']>) => setForm((f) => (f ? { ...f, config: { ...f.config, ...patch } } : f));

  const submit = async () => {
    if (!form || busy) return;
    setBusy('create'); setErr(null);
    const body: CustomerAiProviderInput = form.kind === 'vertex_wif'
      ? { kind: form.kind, display_name: form.display_name, model_id: form.model_id, region: form.region,
          config: { project_id: form.config.project_id, wif_provider: form.config.wif_provider, service_account_email: form.config.service_account_email } }
      : { kind: form.kind, display_name: form.display_name, model_id: form.model_id, endpoint_url: form.endpoint_url, api_key: form.api_key,
          ...(form.kind === 'azure_openai' ? { config: { deployment: form.config.deployment, api_version: form.config.api_version } } : {}) };
    try {
      await createAiProvider(slug, body);
      setForm(null); setMsg('Provider added. Run its check to start using it.');
      await refresh();
    } catch (e) { setErr(errorText(e, 'That did not save.')); }
    finally { setBusy(null); }
  };
  const isOwner = role === 'owner';
  const act = async (id: string, what: 'evaluate' | 'disable' | 'approve') => {
    if (busy) return;
    setBusy(`${what}:${id}`); setErr(null);
    try {
      if (what === 'evaluate') {
        const r = await evaluateAiProvider(slug, id);
        setMsg(`${r.results.filter((x) => x.pass).length} of ${r.results.length} tasks passed`);
      } else if (what === 'approve') {
        await approveAiProvider(slug, id);
        setMsg('Provider approved');
      } else {
        await patchAiProvider(slug, id, { status: 'disabled' });
        setMsg('Provider disabled');
      }
      await refresh();
    } catch (e) { setErr(errorText(e, what === 'evaluate' ? 'The check did not run.' : 'That did not save.')); }
    finally { setBusy(null); }
  };

  const rows = (q.data?.providers ?? []).filter((p) => p.status !== 'disabled');
  return (
    <div id="your-ai-providers" className="dps" tabIndex={-1} role="region" aria-labelledby="dps-cap-h">
      <Card>
        <div className="dps-body">
          <h2 id="dps-cap-h" className="dps-h">Your AI providers</h2>
          {!canManage ? (
            <AskAdmin slug={slug} what="connects your organization's AI provider" subject="Connect our AI provider to GAC Pro" />
          ) : q.isLoading ? <Spinner label="Loading your AI providers" /> : q.error ? (
            <Alert variant="error"><p>Your AI providers could not load.</p><Button compact onClick={() => void q.refetch()}>Retry</Button></Alert>
          ) : (
            <>
              {rows.length === 0 && !form ? (
                <EmptyState title="No provider connected" description="Your own model endpoint can serve AI features that your data mode keeps off ours."
                  action={<Button variant="primary" compact onClick={() => setForm(blank())}>Connect your provider</Button>} />
              ) : null}
              {rows.length ? (
                <ul className="dps-list" aria-label="Your AI providers">
                  {rows.map((p) => (
                    <li key={p.ai_provider_id} className="dps-item">
                      <span>
                        <span>{p.display_name}</span>
                        <span className="dps-dim"> · {p.model_id} · {(waitingForOwner(p) ? p.eval_passed_tasks : p.enabled_tasks).length} task{(waitingForOwner(p) ? p.eval_passed_tasks : p.enabled_tasks).length === 1 ? '' : 's'}</span>
                      </span>
                      <Badge tone={(waitingForOwner(p) ? WAITING : STATUS[p.status]).tone}>{(waitingForOwner(p) ? WAITING : STATUS[p.status]).label}</Badge>
                      <span className="dps-row">
                        {waitingForOwner(p) ? (isOwner ? (
                          <Button variant="primary" compact aria-disabled={!!busy || undefined} aria-label={`Approve ${p.display_name}`}
                            onClick={() => void act(p.ai_provider_id, 'approve')}>{busy === `approve:${p.ai_provider_id}` ? 'Approving…' : 'Approve'}</Button>
                        ) : (
                          <AskAdmin slug={slug} compact verb="approve" roles={['owner']} lead="An owner"
                            what={`approves ${p.display_name} before it receives your data`} subject={`Approve our AI provider ${p.display_name} in GAC Pro`} />
                        )) : null}
                        <Button variant={p.status === 'active' ? 'ghost' : 'secondary'} compact aria-disabled={!!busy || undefined}
                          aria-label={`Run the check for ${p.display_name}`} onClick={() => void act(p.ai_provider_id, 'evaluate')}>
                          {busy === `evaluate:${p.ai_provider_id}` ? 'Checking…' : 'Run check'}
                        </Button>
                        <Button variant="ghost" compact aria-disabled={!!busy || undefined} aria-label={`Disable ${p.display_name}`}
                          onClick={() => void act(p.ai_provider_id, 'disable')}>Disable</Button>
                      </span>
                      {p.wif_subject && p.status !== 'active' ? (
                        <details className="dps-item-form">
                          <summary>Google Cloud setup</summary>
                          <p className="dps-dim">Let this identity use Vertex AI in your project:</p>
                          <code>{p.wif_subject}</code>
                        </details>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
              {rows.length && !form ? <div className="dps-row"><Button variant="secondary" compact onClick={() => setForm(blank())}>Add a provider</Button></div> : null}
              {form ? (
                <div className="dps-confirm" role="group" aria-labelledby="dps-cap-form-h">
                  <h3 id="dps-cap-form-h" className="dps-h">Connect a provider</h3>
                  <Field id="cap-kind" label="Type">
                    <Select value={form.kind} onChange={(e) => set({ kind: e.currentTarget.value as CustomerAiProviderKind })}>
                      {(Object.keys(KIND_LABEL) as CustomerAiProviderKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                    </Select>
                  </Field>
                  <Field id="cap-name" label="Name"><Input value={form.display_name} onChange={(e) => set({ display_name: e.currentTarget.value })} /></Field>
                  <Field id="cap-model" label="Model"><Input value={form.model_id} placeholder={form.kind === 'vertex_wif' ? 'gemini-2.5-pro' : 'gpt-4o'} onChange={(e) => set({ model_id: e.currentTarget.value })} /></Field>
                  {form.kind === 'vertex_wif' ? (
                    <>
                      <Field id="cap-region" label="Region">
                        <Select value={form.region} onChange={(e) => set({ region: e.currentTarget.value })}>{REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}</Select>
                      </Field>
                      <Field id="cap-project" label="Project ID"><Input value={form.config.project_id ?? ''} onChange={(e) => setCfg({ project_id: e.currentTarget.value })} /></Field>
                      <Field id="cap-wif" label="Workload identity provider"><Input value={form.config.wif_provider ?? ''} onChange={(e) => setCfg({ wif_provider: e.currentTarget.value })} /></Field>
                      <Field id="cap-sa" label="Service account"><Input value={form.config.service_account_email ?? ''} onChange={(e) => setCfg({ service_account_email: e.currentTarget.value })} /></Field>
                    </>
                  ) : (
                    <>
                      <Field id="cap-endpoint" label="Endpoint (https)"><Input value={form.endpoint_url ?? ''} inputMode="url" onChange={(e) => set({ endpoint_url: e.currentTarget.value })} /></Field>
                      {form.kind === 'azure_openai' ? (
                        <Field id="cap-deployment" label="Deployment"><Input value={form.config.deployment ?? ''} onChange={(e) => setCfg({ deployment: e.currentTarget.value })} /></Field>
                      ) : null}
                      <Field id="cap-key" label="API key"><Input type="password" autoComplete="off" value={form.api_key ?? ''} onChange={(e) => set({ api_key: e.currentTarget.value })} /></Field>
                    </>
                  )}
                  {err ? <p className="dps-err" role="alert">{err}</p> : null}
                  <div className="dps-row">
                    <Button variant="primary" compact aria-disabled={!!busy || undefined} onClick={() => void submit()}>{busy === 'create' ? 'Saving…' : 'Connect'}</Button>
                    <Button variant="ghost" compact onClick={() => { setForm(null); setErr(null); }}>Cancel</Button>
                  </div>
                </div>
              ) : err ? <p className="dps-err" role="alert">{err}</p> : null}
            </>
          )}
          <p role="status" className="sr-only">{msg}</p>
        </div>
      </Card>
    </div>
  );
}
